import React, {useState, useEffect, memo} from 'react';
import {BarChart} from '@mui/x-charts';
import {Select} from 'antd';
import {CustomInput} from './ui';
import {useSelector} from 'react-redux';
import {fetchFilteredExpensesApi} from '../api/services/allExpenses';
import {
  AreaChart,
  XAxis,
  YAxis,
  Tooltip,
  Area,
  ResponsiveContainer,
} from 'recharts';
const {Option} = Select;

const getCurrentWeekDays = () => {
  const today = new Date();
  const dayOfWeek = today.getDay();
  const startOfWeek = new Date(today);
  startOfWeek.setDate(today.getDate() - dayOfWeek);

  const weekDays = [];
  const dayNames = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];
  for (let i = 0; i < 7; i++) {
    const day = new Date(startOfWeek);
    day.setDate(startOfWeek.getDate() + i);
    weekDays.push(dayNames[day.getDay()]);
  }

  return weekDays;
};

const getLast6Years = () => {
  const currentYear = new Date().getFullYear();
  return Array.from({length: 6}, (_, i) => currentYear - i).reverse();
};

const getAllMonths = () => [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];
const CustomTooltip = ({active, payload, label, symbol, activeLine}) => {
  if (!active || !payload || !payload.length) return null;

  const value = payload[0].value;

  const formatValue = (val) => {
    if (val >= 10000000) return `${symbol}${(val / 1000000).toFixed(1)}B`;
    if (val >= 1000000) return `${symbol}${(val / 1000000).toFixed(1)}M`;
    return `${symbol}${val.toFixed(2)}`;
  };

  const labelMap = {
    income: 'Income',
    expense: 'Expense',
    profit: 'Net Income',
  };

  const colorMap = {
    income: '#4CAF50',
    expense: '#FF5733',
    profit: '#002DD1',
  };

  return (
    <div className="bg-white rounded-lg px-4 py-3 shadow-lg border border-gray-200">
      <p className="text-sm text-gray-500 mb-1">{label}</p>

      <div className="flex items-center gap-2">
        <span
          className="w-3 h-3 rounded-full"
          style={{backgroundColor: colorMap[activeLine]}}
        />
        <span className="text-sm font-semibold text-gray-700">
          {labelMap[activeLine]}:
        </span>
        <span className="text-sm font-bold text-gray-900">
          {formatValue(value)}
        </span>
      </div>
    </div>
  );
};

const ExpenseGraph = ({
  selectedProject,
  selectedMilestone,
  selectedDuration,
  startDate,
  endDate,
  projectCurrency,
}) => {
  const user = useSelector((state) => state.auth.user);
  const {expenseByProjectId} = useSelector((state) => state.expense);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expenseData, setExpenseData] = useState([]);
  // const [showIncome, setShowIncome] = useState(true);
  // const [showExpense, setShowExpense] = useState(true);
  // const [showProfit, setShowProfit] = useState(true);
  const [activeLine, setActiveLine] = useState('profit');

  const symbol = useSelector((state) => {
    const currencyId = projectCurrency || localStorage.getItem('baseCurrency');

    return (
      state.currency.currencies.find((c) => c._id === currencyId)?.symbol ||
      'N/A'
    );
  });
  const getEmptyDataset = () => {
    if (selectedDuration === 'last12months') {
      return getAllMonths().map((month) => ({
        day: month,
        totalIncome: 0,
        totalExpense: 0,
        profit: 0,
      }));
    } else if (selectedDuration === 'currentweek') {
      return getCurrentWeekDays().map((day) => ({
        day,
        totalIncome: 0,
        totalExpense: 0,
        profit: 0,
      }));
    } else if (selectedDuration === 'last6years') {
      return getLast6Years().map((year) => ({
        day: year.toString(),
        totalIncome: 0,
        totalExpense: 0,
        profit: 0,
      }));
    } else if (selectedDuration === 'all') {
      const currentYear = new Date().getFullYear();
      const years = [];
      for (let i = currentYear; i >= currentYear - 10; i--) {
        years.push(i.toString());
      }
      return years.map((year) => ({
        day: year,
        totalIncome: 0,
        totalExpense: 0,
        profit: 0,
      }));
    }
    return [];
  };

  const fetchFilteredExpenses = async (
    selectedProject,
    selectedMilestone,
    startDate,
    endDate
  ) => {
    try {
      setLoading(true);
      setError(null);
      const allExpenses = await fetchFilteredExpensesApi({
        userRole: user.userRole.name,
        selectedProject,
        selectedMilestone,
        startDate,
        endDate,
      });
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth();
      const currentWeekDays = getCurrentWeekDays();

      let groupedData = {};

      if (selectedDuration === 'last12months') {
        const last12Months = [];
        for (let i = 11; i >= 0; i--) {
          const date = new Date();
          date.setMonth(date.getMonth() - i);
          last12Months.push(date.toLocaleString('default', {month: 'short'}));
        }

        groupedData = last12Months.reduce((acc, month) => {
          acc[month] = {totalIncome: 0, totalExpense: 0, profit: 0};
          return acc;
        }, {});

        allExpenses.forEach((expense) => {
          const expenseDate = new Date(expense.date);
          const month = expenseDate.toLocaleString('default', {
            month: 'short',
          });

          if (groupedData[month]) {
            const amountInBaseCurrency = expense.amount;
            const finalAmount = projectCurrency
              ? amountInBaseCurrency
              : amountInBaseCurrency / expense.conversionRate;
            if (expense.type.toLowerCase() === 'income') {
              groupedData[month].totalIncome += finalAmount;
            } else if (expense.type.toLowerCase() === 'expense') {
              groupedData[month].totalExpense += finalAmount;
            }
            groupedData[month].profit = Math.max(
              0,
              groupedData[month].totalIncome - groupedData[month].totalExpense
            );
          }
        });
      } else if (selectedDuration === 'currentweek') {
        groupedData = currentWeekDays.reduce((acc, day) => {
          acc[day] = {totalIncome: 0, totalExpense: 0, profit: 0};
          return acc;
        }, {});

        allExpenses.forEach((expense) => {
          const expenseDate = new Date(expense.date);
          if (
            expenseDate.getFullYear() !== currentYear ||
            expenseDate.getMonth() !== currentMonth
          )
            return;

          const day = expenseDate.toLocaleString('default', {
            weekday: 'long',
          });

          if (groupedData[day]) {
            const amountInBaseCurrency = expense.amount;
            const finalAmount = projectCurrency
              ? amountInBaseCurrency
              : amountInBaseCurrency / expense.conversionRate;
            if (expense.type.toLowerCase() === 'income') {
              groupedData[day].totalIncome += finalAmount;
            } else if (expense.type.toLowerCase() === 'expense') {
              groupedData[day].totalExpense += finalAmount;
            }
            groupedData[day].profit = Math.max(
              0,
              groupedData[day].totalIncome - groupedData[day].totalExpense
            );
          }
        });
      } else if (selectedDuration === 'last6years') {
        groupedData = getLast6Years().reduce((acc, year) => {
          acc[year] = {totalIncome: 0, totalExpense: 0, profit: 0};
          return acc;
        }, {});

        allExpenses.forEach((expense) => {
          const expenseYear = new Date(expense.date).getFullYear();
          if (!groupedData[expenseYear]) return;

          const amountInBaseCurrency = expense.amount;
          const finalAmount = projectCurrency
            ? amountInBaseCurrency
            : amountInBaseCurrency / expense.conversionRate;
          if (expense.type.toLowerCase() === 'income') {
            groupedData[expenseYear].totalIncome += finalAmount;
          } else if (expense.type.toLowerCase() === 'expense') {
            groupedData[expenseYear].totalExpense += finalAmount;
          }
          groupedData[expenseYear].profit = Math.max(
            0,
            groupedData[expenseYear].totalIncome -
              groupedData[expenseYear].totalExpense
          );
        });
      } else if (selectedDuration === 'all') {
        // Group data by all available years
        allExpenses.forEach((expense) => {
          const expenseYear = new Date(expense.date).getFullYear().toString();
          if (!groupedData[expenseYear]) {
            groupedData[expenseYear] = {
              totalIncome: 0,
              totalExpense: 0,
              profit: 0,
            };
          }

          const amountInBaseCurrency = expense.amount;
          const finalAmount = projectCurrency
            ? amountInBaseCurrency
            : amountInBaseCurrency / expense.conversionRate;
          if (expense.type.toLowerCase() === 'income') {
            groupedData[expenseYear].totalIncome += finalAmount;
          } else if (expense.type.toLowerCase() === 'expense') {
            groupedData[expenseYear].totalExpense += finalAmount;
          }

          groupedData[expenseYear].profit = Math.max(
            0,
            groupedData[expenseYear].totalIncome -
              groupedData[expenseYear].totalExpense
          );
        });
      }
      const formattedData = Object.keys(groupedData).map((key) => ({
        day: key,
        totalIncome: groupedData[key].totalIncome,
        totalExpense: groupedData[key].totalExpense,
        profit: groupedData[key].profit,
      }));
      setExpenseData(formattedData);
    } catch (error) {
      console.error('Error fetching or processing data:', error.message);
      setError('Failed to load expense data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (
      selectedProject &&
      selectedMilestone &&
      (selectedDuration === 'all' || (startDate && endDate))
    ) {
      fetchFilteredExpenses(
        selectedProject,
        selectedMilestone,
        startDate,
        endDate
      );
    }
  }, [
    selectedProject,
    selectedMilestone,
    startDate,
    endDate,
    selectedDuration,
    expenseByProjectId,
    projectCurrency,
  ]);

  // const handleIncomeChange = () => setShowIncome((prev) => !prev);
  // const handleExpenseChange = () => setShowExpense((prev) => !prev);
  // const handleProfitChange = () => setShowProfit((prev) => !prev);

  if (loading)
    return (
      <div className="flex justify-center items-center h-[100%] w-full">
        <div className="spinner"></div>
      </div>
    );

  // if (error) return <p className="text-red-500">Error: {error}</p>;
  return (
    <div className="rounded-lg bg-white font-customFont h-full w-full flex flex-col">
      {/* Checkbox Controls */}
      <div className="flex flex-wrap gap-3">
        <label className="flex items-center cursor-pointer">
          <CustomInput
            type="radio"
            name="line"
            checked={activeLine === 'profit'}
            onChange={() => setActiveLine('profit')}
            className="custom-radioNetIncome me-1"
          />
          <span className="text-black">Net Income</span>
        </label>
        <label className="flex items-center me-2 cursor-pointer">
          <CustomInput
            type="radio"
            name="line"
            checked={activeLine === 'income'}
            onChange={() => setActiveLine('income')}
            className="custom-radioIncome me-1"
          />
          <span className="text-black">Income</span>
        </label>

        <label className="flex items-center me-2 cursor-pointer">
          <CustomInput
            type="radio"
            name="line"
            checked={activeLine === 'expense'}
            onChange={() => setActiveLine('expense')}
            className="custom-radioExpense me-1"
          />
          <span className="text-black">Expense</span>
        </label>
      </div>

      <div className="flex-1 min-h-0 relative">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={expenseData.length > 0 ? expenseData : getEmptyDataset()}
            margin={{top: 20, right: 30, left: 0, bottom: 0}}
          >
            <defs>
              {activeLine === 'income' && (
                <linearGradient id="colorIncome" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#4CAF50" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#4CAF50" stopOpacity={0} />
                </linearGradient>
              )}

              {activeLine === 'expense' && (
                <linearGradient id="colorExpense" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#FF5733" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#FF5733" stopOpacity={0} />
                </linearGradient>
              )}

              {activeLine === 'profit' && (
                <linearGradient id="colorProfit" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#002DD1" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#002DD1" stopOpacity={0} />
                </linearGradient>
              )}
            </defs>

            <XAxis
              dataKey="day"
              tick={{fontSize: 12, fill: '#666'}}
              axisLine={{stroke: '#333', strokeWidth: 1}}
              tickLine={{stroke: '#333', strokeWidth: 1}}
            />
            <YAxis
              tick={{fontSize: 12, fill: '#666'}}
              axisLine={{stroke: '#333', strokeWidth: 1}}
              tickLine={{stroke: '#333', strokeWidth: 1}}
              tickFormatter={(value) =>
                value != null
                  ? value >= 10000000
                    ? `${(value / 1000000).toFixed(1)}B`
                    : value >= 1000000
                    ? `${(value / 1000000).toFixed(1)}M`
                    : value.toFixed(2)
                  : '0'
              }
            />
            <Tooltip
              content={
                <CustomTooltip symbol={symbol} activeLine={activeLine} />
              }
            />

            {activeLine === 'income' && (
              <Area
                type="monotone"
                dataKey="totalIncome"
                stroke="#4CAF50"
                fill="url(#colorIncome)"
                strokeWidth={2}
              />
            )}

            {activeLine === 'expense' && (
              <Area
                type="monotone"
                dataKey="totalExpense"
                stroke="#FF5733"
                fill="url(#colorExpense)"
                strokeWidth={2}
              />
            )}

            {activeLine === 'profit' && (
              <Area
                type="monotone"
                dataKey="profit"
                stroke="#002DD1"
                fill="url(#colorProfit)"
                strokeWidth={2}
              />
            )}
          </AreaChart>
        </ResponsiveContainer>

        {expenseData.length === 0 && (
          <div className="absolute flex flex-col items-center justify-center z-[111] inset-0 backdrop-blur-sm bg-white-50/60 ">
            <i className="fa-solid fa-sack-dollar text-gray-400 text-5xl"></i>
            <p className="text-gray-400 mt-4">
              Please create an Expense to view its details.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(ExpenseGraph);
